import { DevTool } from "@hookform/devtools";
import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { useForm } from "react-hook-form";
import { Button } from "primereact/button";
import DataGrid from '../../components/common/dataGrid/dataGrid';
import ButtonWithSearch from '../../components/common/buttonWithSearch';
import { commonGetApi, commonPostApi } from "../../utils/apiService";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";
import { generateColumnMetadata, fetchOptions, orderObj, Feature, convertToSearchField } from "../../utils/utils";
import PrimaryHeading from '../../components/common/primaryHeading';
import PrimarySideBar from '../../components/common/sideBar/primarySideBar';
import InputIterator from '../../components/common/inputIterator';
import AddButtonSection from '../../components/common/AddButtonSection';
import { organizationFormRender, OrganizationsTableColumns } from "../../utils/serviceData";
import { showToast } from '../../components/common/toast';
import { TOAST_TYPE, COMMON_MSG, FEATURE_MSG } from "../../constants/toast";
import { SORT_ORDER } from "../../constants/sortOrder";
import { useNavigate } from "react-router-dom";
import "../../assets/styles/organizations.scss";
import { dateFormat, generateValidObject } from "../../utils/utils";
import AddImageSection from '../../components/common/AddImageSection';


export default function OrganizationList() {
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    reset,
    setValue,
    trigger,
    clearErrors
  } = useForm({mode: "onChange",reValidateMode: "onChange"});
  const navigate = useNavigate();
  const menuLeft = useRef(null);
  const [columns, setColumns] = useState([]);
  const [values, setValues] = useState([]);
  const [initLoading, setInitLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loader, setLoader] = useState(false);
  const [existingOrgnames, setExistingOrgnames] = useState([]);
  const [addOrganizationVisible, setAddOrganizationVisible] = useState(false);
  const [editUserVisible, setEditUserVisible] = useState(false);
  const [editOrgDetails, setEditOrgDetails] = useState({});
  const [formRender, setFormRender] = useState(organizationFormRender);
  const searchFields=["organization_name","organization_code","description","address","contact_person","contact_number"]
  const [uploadedImg, setUploadedImg] = useState({});
  const [navigator, setNavigator] = useState({
    page: 1,
    first: 0,
    rows: 10,
    totalRecords: 1,
    sortBy: "organization_name",
    sortDir: SORT_ORDER.ASC,
    searchCriteria:convertToSearchField(searchFields, "")
  });
  const fields = [
    "organization_name",
    "description",
    "address",
    "city",
    "district",
    "state",
    "pin_code",
    "profile_picture",
    "contact_person",
    "contact_number",
  ];

  function onPageChange(event) {
    setLoading(true);
    setNavigator((prev) => ({
      ...prev,
      first: event?.first,
      page: event.page + 1,
    }));
    init({
      ...navigator,
      first: event?.first,
      page: event.page + 1,
    });
  }

  function onRowCountChange(e) {
    setLoading(true);
    setNavigator((prev) => ({
      ...prev,
      rows: e?.target?.value,
      page: 1,
      first: 0,
    }));
    init({
      ...navigator,
      rows: e?.target?.value,
      page: 1,
      first: 0,
    });
  }

const GetExisistOrganizationDetails = async () => {
  try {
    const existingRes = await commonGetApi(
      API_ENDPOINTS.GET_EXISTING_ORGANIZATIONS
    );

    setExistingOrgnames(
      (existingRes?.data?.data?.rows || [])
        .map(o => o.organization_name?.toLowerCase().trim())
        .filter(Boolean)
    );
  } catch (error) {
    console.error("Failed to fetch organizations", error);
  }
};


  const init = async (navigator) => {
    setInitLoading(true);
    let currentObj = {
      query: {
        page: navigator?.page,
        limit: navigator?.rows,
        sortFields:
          navigator?.sortFields || [[navigator?.sortBy, navigator?.sortDir]],
        searchCriteria: navigator?.searchCriteria,
      },
    };

    Promise.all([commonPostApi(API_ENDPOINTS.ORGANIZATION_DETAILS, currentObj)
    ])
      .then(([data]) => {
        const orgData = data?.data?.data;

        setNavigator((prev) => ({
          ...prev,
          totalRecords: data?.data?.total,
          page: data?.data?.currentPage,
        }));
        setColumns(
          orderObj(
            [
              "organization_name",
              "organization_code",
              "description",
              "address",
              "contact_person",
              "contact_number",
            ],
            generateColumnMetadata({
              data: orgData,
              alias: {
                organization_name: "organization",
                organization_code: "org_id",
                contact_number: "contact_no",
              },
              defaultColumnsArr:OrganizationsTableColumns
            })
          )
        );
        setValues(orgData);
        setLoading(false);
        setInitLoading(false);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onSortClick = (event) => {
    setLoading(true);
    const sortOrder = event?.sortOrder === 1 ? SORT_ORDER.ASC : SORT_ORDER.DESC;
    setNavigator((prev) => ({
      ...prev,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    }));
    init({
      ...navigator,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    });
  };

  const onSubmit = async (data) => {
    if (loader) return;
    setLoader(true);
    if (uploadedImg?.size > 800 * 1024) {
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.VALIDATION.FILE_SIZE_EXCEEDED);
      setLoader(false);
      return;
    }
    let headers = {
      "Content-Type": "application/json",
    };
    let addCamp = {
      ...data,
      start_date: dateFormat(data["start_date"]),
      end_date: dateFormat(data["end_date"]),
    }
    let finalData = addCamp;
    if (uploadedImg && uploadedImg.size && uploadedImg.size > 0) {
      finalData = new FormData();
      finalData.append("profile_picture", uploadedImg);
      finalData.append("_payload", JSON.stringify(addCamp));
      headers = {
        "Content-Type": "multipart/form-data",
      };
    } else {
      headers = {
        "Content-Type": "application/json",
      };
    }
    await commonPostApi(API_ENDPOINTS.ADD_ORGANIZATION_DETAILS, finalData, {
      headers,
    })
      .then((data) => {
        showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.ORGANIZATION.ADDED);
        GetExisistOrganizationDetails();
        setAddOrganizationVisible(false);
        init(navigator);
        reset({});
        setLoader(false);
      })
      .catch((err) => {
        showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
        setLoader(false);
      });
  };

  const onEdit = async (data) => {
    if (loader) return;
    setLoader(true);
    let headers = {};
    let validObj = generateValidObject(data);
    const dob = dateFormat(validObj["dob"]);
    validObj = {
      ...validObj,
      dob: dob,
      organization_id: editOrgDetails?.organization_id
    };
    let finaldata = validObj;
    if (uploadedImg && uploadedImg.size && uploadedImg.size > 0) {
      finaldata = new FormData();
      finaldata.append("profile_picture", uploadedImg);
      finaldata.append("_payload", JSON.stringify(validObj));
      headers = {
        "Content-Type": "multipart/form-data",
      };
    } else {
      headers = {
        "Content-Type": "application/json",
      };
    }
    await commonPostApi(
      API_ENDPOINTS.EDIT_ORGANIZATION_DETAILS,
      finaldata,
      {
        headers,
      }
    )
      .then((data) => {
        showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.ORGANIZATION.UPDATED);
        setEditUserVisible(false);
        init(navigator);
        setExistingOrgnames((prev) => [
          ...prev.filter(
            (name) =>
              name !== editOrgDetails?.organization_name?.toLowerCase()?.trim(),
          ),
          validObj.organization_name?.toLowerCase()?.trim(),
        ]);
        reset();
        setLoader(false);
      })
      .catch((err) => {
        showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
        setLoader(false);
      });
  };

  // const onDelete = async () => {
  //   let headers = {
  //     "Content-Type": "application/json",
  //   };
  //   await commonPostApi(
  //     "api/deleteuserdetails",
  //     { user_id: visible?.id },
  //     {
  //       headers,
  //     }
  //   )
  //     .then((data) => {
  //       if (data?.status == 200) {
  //         showToast("success", "Deleted user Successfully");
  //         init(navigator);
  //         setVisible((prev) => ({
  //           ...prev,
  //           show: false,
  //         }));
  //       }
  //     })
  //     .catch((err) => {
  //       showToast("error", "Something went wrong");
  //     });
  // };

  const button = [
    {
      type: "primarySuccessButton",
      label: "Add Organization",
      icon: "pi pi-plus",
      class: "mt-2 w-full pr-3",
      onClick: () => {
        setEditOrgDetails({});
        setUploadedImg({});
        clearErrors();
        reset({
          organization_name: "",
          description: "",
          address: "",
          city: "",
          district: "",
          state: "",
          pin_code: "",
          contact_person: "",
          contact_number: "",
          profile_picture: "",
        });
        setAddOrganizationVisible(true);
      },
    },
  ];
  const itemRenderer = (item, props) => (
    <div className="p-menuitem-content">
      {item.label === "Edit" &&
        Feature?.featureVerify(Feature.listOfFeature.organizationEdit) && (
          <Button
            className="card-button-edit-secound"
            label="Edit"
            onClick={() => {
              const data = props?.props?.itemProp;

              reset(data); // set form values
              clearErrors(); // remove validation errors
              setEditOrgDetails(data);
              setEditUserVisible(true);

              const imgUrl = data?.profile_picture
                ? `${process.env?.REACT_APP_CURRENT_URL}${API_ENDPOINTS.SEARCH_IMAGE_BY_ID(data?.organization_id)}`
                : null;

              setUploadedImg(imgUrl);
            }}
          />
        )}
      {/* {item.label === "Delete" && (
        <Button
          className="card-button-delete"
          label="Delete"
          onClick={() => {
            setVisible({
              id: props?.props?.itemProp?.user_id,
              show: true,
            });
          }}
        />
      )} */}
    </div>
  );

  const AddCampbuttons = [
    {
      type: "primarySuccessButton",
      label: "Submit",
      class: "col-4",
      onClick: handleSubmit(onSubmit),
    },
    {
      type: "primaryCancelButton",
      label: "Cancel",
      class: "col-4",
      onClick: () => {
        reset();
        clearErrors();
        setUploadedImg({});
        setAddOrganizationVisible(false);
      },
    },
  ];
  const EditCampbuttons = [
    {
      type: "primarySuccessButton",
      label: "Submit",
      class: "col-4",
      disabled: loader || (Object.keys(dirtyFields).length === 0 && !uploadedImg),
      onClick: handleSubmit(onEdit),
    },
    {
      type: "primaryCancelButton",
      label: "Cancel",
      class: "col-4",
      onClick: () => {
        clearErrors();
        setEditUserVisible(false);

      },
    },
  ];

  // const submitButtons = [
  //   {
  //     type: "primarySuccessButton",
  //     label: "Yes",
  //     class: "w-8rem pr-3",
  //     onClick: () => {
  //       // onDelete();
  //     },
  //   },
  //   {
  //     type: "secondaryButton",
  //     label: "No",
  //     class: "w-7rem",
  //     onClick: () => {
  //       setVisible((prev) => ({
  //         ...prev,
  //         show: false,
  //       }));
  //     },
  //   },
  // ];

  const initiateForm = () => {
    fetchOptions("User_TYpe", (userType) =>
      setFormRender((prev) =>
        prev.map((item) => {
          if (item?.name == "user_type") return { ...item, options: userType };
          else return item;
        })
      )
    );
  };
  const onRowClick = (event) => {
    navigate(`/organizations/${event?.data?.organization_id}`);
  };

  useEffect(() => {
    initiateForm();
    GetExisistOrganizationDetails();
    init(navigator);
  }, []);

useEffect(() => {
  if (!existingOrgnames.length) return;

  setFormRender((prev) =>
    prev.map((field) => {
      if (field.name === "organization_name") {
        return {
          ...field,
          rules: {
            ...field.rules,
            validate: (value) => {
              if (!value) return true;

              const normalized = value.toLowerCase().trim();

              if (
                editUserVisible &&
                normalized ===
                  editOrgDetails?.organization_name?.toLowerCase()?.trim()
              ) {
                return true;
              }

              if (existingOrgnames.includes(normalized)) {
                return "Organization name already exists";
              }

              return true;
            },
          },
        };
      }

      return field;
    })
  );
}, [existingOrgnames, editUserVisible, editOrgDetails]);

  const handleSeachChange = (value) => {
  
    setNavigator((prev) => ({
      ...prev,
       searchCriteria: convertToSearchField(searchFields, value),
    }));
    init({
      ...navigator,
       searchCriteria: convertToSearchField(searchFields, value),
    });
  };
  return (
    <div className="organization-container container">
      <PrimaryHeading className={"mt-2 mb-3"} />
      <DataGrid
        header={
          <ButtonWithSearch
            buttons={button}
            searchHeaderLable={"Organizations"}
            searchPlaceHolder={"Search organizations"}
            isSearchEnabled={true}
            isCalenderEnabled={false}
            isButtonEnabledLeft={false}
            isButtonEnabledRight={Feature?.featureVerify(
              Feature.listOfFeature.organizationAdd,
            )}
            loading={initLoading}
            styles={{ gridTemplateColumns: "1fr 400px" }}
            onChange={handleSeachChange}
          />
        }
        onSortClick={onSortClick}
        onRowCountChange={onRowCountChange}
        onRowClick={onRowClick}
        onPageChange={onPageChange}
        isPaginatorEnabled={true}
        navigator={navigator}
        column={columns}
        value={values}
        ref={menuLeft}
        loading={loading}
        sortable={
          (values && !_.isArray(values)) ||
          (values && values?.length !== 0) ||
          values.length === 0
        }
        optionsView={
          Feature?.featureVerify(Feature.listOfFeature.campEdit) && [
            {
              items: [
                {
                  label: "Edit",
                  icon: "pi pi-pencil",
                  template: itemRenderer,
                },
                {
                  label: "Delete",
                  icon: "pi pi-upload",
                  template: itemRenderer,
                },
              ],
            },
          ]
        }
      />
      {addOrganizationVisible && (
        <PrimarySideBar
          visible={addOrganizationVisible}
          onHide={() => {
            reset();
            clearErrors();
            setUploadedImg({});
            setAddOrganizationVisible(false);
          }}
          header={<div className="sideBar-header">Add Organization</div>}
        >
          <AddImageSection showImg={uploadedImg} setImg={setUploadedImg} />
          <InputIterator
            control={control}
            errors={errors}
            formRender={formRender}
            onInputChange={(name) => {
              if (name === "organization_name") {
                trigger("organization_name");
              }
            }}
          />
          <div className="pt-3">
            <AddButtonSection disabled={loader} buttons={AddCampbuttons} />
          </div>
        </PrimarySideBar>
      )}
      {editUserVisible && (
        <PrimarySideBar
          visible={editUserVisible}
          onHide={() => {
            reset({});
            clearErrors();
            setEditUserVisible(false);
          }}
          header={<div className="sideBar-header">Edit Organization</div>}
        >
          <AddImageSection showImg={uploadedImg} setImg={setUploadedImg} />
          <InputIterator
            control={control}
            errors={errors}
            formRender={formRender}
            onInputChange={(name) => {
              if (name === "organization_name") {
                trigger("organization_name");
              }
            }}
          />
          <div className="pt-3">
            <AddButtonSection disabled={loader} buttons={EditCampbuttons} />
          </div>
        </PrimarySideBar>
      )}
      {/* <CommonModal
        visible={visible?.show}
        header="User Patient"
        onHide={() => setVisible(false)}
      >
        <div>
          <div className="flex justify-content-center pr-4">
            Would you like to delete this user?
          </div>

          <div className="mt-6 ml-2">
            <AddButtonSection buttons={submitButtons} />
          </div>
        </div>
      </CommonModal> */}
      <DevTool control={control} />
    </div>
  );
}
